import type { MapFolder } from './properties'
import { UNFILED_FOLDER_LABEL, compareMapFolders, resolveBucketFolderId } from './properties'

export const UNFILED_BUCKET_ID = 'unfiled'

export type FolderMemberKind =
  | 'property'
  | 'zone'
  | 'blind'
  | 'stand'
  | 'waypoint'
  | 'scouting'
  | 'path'

export type FolderedEntity = {
  id: string
  name?: string
  propertyId?: string
  zoneId?: string | null
  effectiveFolderId?: string
  folderSource?: string
}

export type FolderMember = {
  id: string
  kind: FolderMemberKind
  name: string
  bucketId: string
  isInherited: boolean
  inheritedFromPropertyName: string | null
  propertyId?: string
  zoneId?: string | null
}

export type FolderPropertyGroupRow = {
  member: FolderMember
  depth: number
}

export type FolderPropertyGroup = {
  property: FolderMember
  rows: FolderPropertyGroupRow[]
}

export type FolderBucket = {
  id: string
  folder: MapFolder | null
  title: string
  members: FolderMember[]
  propertyGroups: FolderPropertyGroup[]
  looseMembers: FolderMember[]
}

export type FolderBucketSources = {
  folders: MapFolder[]
  properties: FolderedEntity[]
  zones: FolderedEntity[]
  blinds: FolderedEntity[]
  stands: FolderedEntity[]
  waypoints: FolderedEntity[]
  scouting: FolderedEntity[]
  paths: FolderedEntity[]
}

export function classifyFolderMember(
  source: FolderedEntity,
  kind: FolderMemberKind,
  knownFolderIds: ReadonlySet<string>,
  propertyNamesById: ReadonlyMap<string, string>,
  fallbackName: string,
): FolderMember {
  const resolved = resolveBucketFolderId(source.effectiveFolderId, knownFolderIds)
  const isInherited = resolved !== null && source.folderSource === 'inherited'
  const inheritedFromPropertyName =
    isInherited && source.propertyId ? (propertyNamesById.get(source.propertyId) ?? null) : null
  return {
    id: source.id,
    kind,
    name: source.name?.trim() ? source.name : fallbackName,
    bucketId: resolved ?? UNFILED_BUCKET_ID,
    isInherited,
    inheritedFromPropertyName,
    propertyId: source.propertyId,
    zoneId: source.zoneId,
  }
}

export function groupBucketMembersByProperty(members: FolderMember[]): {
  propertyGroups: FolderPropertyGroup[]
  looseMembers: FolderMember[]
} {
  const propertyGroups: FolderPropertyGroup[] = []
  const membersByPropertyId = new Map<string, FolderMember[]>()
  for (const member of members) {
    if (member.kind !== 'property') continue
    propertyGroups.push({ property: member, rows: [] })
    membersByPropertyId.set(member.id, [])
  }

  const looseMembers: FolderMember[] = []
  for (const member of members) {
    if (member.kind === 'property') continue
    const owned = member.propertyId ? membersByPropertyId.get(member.propertyId) : undefined
    if (owned) {
      owned.push(member)
      continue
    }
    looseMembers.push(member)
  }

  for (const group of propertyGroups) {
    group.rows = nestUnderZones(membersByPropertyId.get(group.property.id) ?? [])
  }

  return { propertyGroups, looseMembers }
}

function nestUnderZones(members: FolderMember[]): FolderPropertyGroupRow[] {
  const zoneIds = new Set(
    members.filter((member) => member.kind === 'zone').map((member) => member.id),
  )
  const childrenByZoneId = new Map<string, FolderMember[]>()
  const topLevel: FolderMember[] = []
  for (const member of members) {
    if (member.kind === 'zone') continue
    const zoneId = member.zoneId
    if (zoneId && zoneIds.has(zoneId)) {
      const existing = childrenByZoneId.get(zoneId)
      if (existing) {
        existing.push(member)
        continue
      }
      childrenByZoneId.set(zoneId, [member])
      continue
    }
    topLevel.push(member)
  }

  const rows: FolderPropertyGroupRow[] = []
  for (const member of members) {
    if (member.kind !== 'zone') continue
    rows.push({ member, depth: 0 })
    for (const child of childrenByZoneId.get(member.id) ?? []) {
      rows.push({ member: child, depth: 1 })
    }
  }
  for (const member of topLevel) {
    rows.push({ member, depth: 0 })
  }
  return rows
}

export function buildFolderBuckets(sources: FolderBucketSources): FolderBucket[] {
  const sortedFolders = [...sources.folders].sort(compareMapFolders)
  const knownFolderIds = new Set(sortedFolders.map((folder) => folder.id))
  const propertyNamesById = new Map(
    sources.properties.map((p) => [p.id, p.name ?? ''] as const),
  )

  const members: FolderMember[] = [
    ...sources.properties.map((property) =>
      classifyFolderMember(property, 'property', knownFolderIds, propertyNamesById, 'Property'),
    ),
    ...sources.zones.map((zone) =>
      classifyFolderMember(zone, 'zone', knownFolderIds, propertyNamesById, 'Zone'),
    ),
    ...sources.blinds.map((blind) =>
      classifyFolderMember(blind, 'blind', knownFolderIds, propertyNamesById, 'Blind'),
    ),
    ...sources.stands.map((stand) =>
      classifyFolderMember(stand, 'stand', knownFolderIds, propertyNamesById, 'Stand'),
    ),
    ...sources.waypoints.map((waypoint) =>
      classifyFolderMember(waypoint, 'waypoint', knownFolderIds, propertyNamesById, 'Waypoint'),
    ),
    ...sources.scouting.map((spot) =>
      classifyFolderMember(spot, 'scouting', knownFolderIds, propertyNamesById, 'Scouting pin'),
    ),
    ...sources.paths.map((path) =>
      classifyFolderMember(path, 'path', knownFolderIds, propertyNamesById, 'Trail'),
    ),
  ]

  const membersByBucket = new Map<string, FolderMember[]>()
  for (const member of members) {
    const existing = membersByBucket.get(member.bucketId)
    if (existing) {
      existing.push(member)
      continue
    }
    membersByBucket.set(member.bucketId, [member])
  }

  const toBucket = (
    id: string,
    folder: MapFolder | null,
    title: string,
  ): FolderBucket => {
    const bucketMembers = membersByBucket.get(id) ?? []
    return {
      id,
      folder,
      title,
      members: bucketMembers,
      ...groupBucketMembersByProperty(bucketMembers),
    }
  }

  const buckets: FolderBucket[] = sortedFolders.map((folder) =>
    toBucket(folder.id, folder, folder.name),
  )

  buckets.push(toBucket(UNFILED_BUCKET_ID, null, UNFILED_FOLDER_LABEL))

  return buckets
}

export function isMapFolderVisible(
  effectiveFolderId: string | undefined,
  params: { hiddenBucketIds: ReadonlySet<string>; knownFolderIds: ReadonlySet<string> },
): boolean {
  if (params.hiddenBucketIds.size === 0) return true
  const bucketId =
    resolveBucketFolderId(effectiveFolderId, params.knownFolderIds) ?? UNFILED_BUCKET_ID
  return !params.hiddenBucketIds.has(bucketId)
}

export function folderMemberKindLabel(kind: FolderMemberKind): string {
  switch (kind) {
    case 'property':
      return 'Property'
    case 'zone':
      return 'Zone'
    case 'blind':
      return 'Blind'
    case 'stand':
      return 'Stand'
    case 'waypoint':
      return 'Waypoint'
    case 'scouting':
      return 'Scouting'
    case 'path':
      return 'Trail'
  }
}
